// MobileSidebar.jsx - Mobile version of Sidebar
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import {
  FiMessageSquare,
  FiSearch,
  FiChevronDown,
  FiChevronUp,
  FiTrash2,
  FiPlus,
  FiX,
  FiEdit2,
  FiCopy,
} from "react-icons/fi";
import { HiDotsHorizontal } from "react-icons/hi";
import { SiChatbot } from "react-icons/si";
import { useNavigate, useLocation } from "react-router-dom";
import ENDPOINTS from "../../api/endpoint";
import SearchModal from "../SearchModal";
import { showErrorToast, showSuccessToast } from "../Toast";

export default function MobileSidebar({
  onRequestDeleteConversation,
  refreshTrigger,
  isOpen,
  onClose,
}) {
  const [showHistory, setShowHistory] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(null);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [chatbots, setChatbots] = useState([]);
  const [showPromptTemplates, setShowPromptTemplates] = useState(true);
  const [isPromptTemplateModalOpen, setIsPromptTemplateModalOpen] =
    useState(false);
  const [promptTemplateName, setPromptTemplateName] = useState("");
  const [promptTemplate, setPromptTemplate] = useState("");
  const [promptTemplates, setPromptTemplates] = useState([]);
  const [editingPromptTemplateId, setEditingPromptTemplateId] = useState(null);
  const [isDeletePromptTemplateModalOpen, setIsDeletePromptTemplateModalOpen] =
    useState(false);
  const [promptTemplateToDelete, setPromptTemplateToDelete] = useState(null);
  const [isDeleteChatbotModalOpen, setIsDeleteChatbotModalOpen] =
    useState(false);
  const [chatbotToDelete, setChatbotToDelete] = useState(null);
  const [showChatbots, setShowChatbots] = useState(true);
  const [isChatbotModalOpen, setIsChatbotModalOpen] = useState(false);
  const [activeChatbotId, setActiveChatbotId] = useState(null);
  const [editingChatbotId, setEditingChatbotId] = useState(null);
  const [newChatbotName, setNewChatbotName] = useState("");
  const [chatbotNamespace, setChatbotNamespace] = useState("");
  const [chatbotDescription, setChatbotDescription] = useState("");
  const [chatbotModel, setChatbotModel] = useState("gpt-5-mini");
  const [chatbotTone, setChatbotTone] = useState("professional");
  const [chatbotResponseLength, setChatbotResponseLength] = useState("medium");
  const [chatbotSystemPrompt, setChatbotSystemPrompt] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const [chatbotConversations, setChatbotConversations] = useState({});
  const [openChatbotMenuId, setOpenChatbotMenuId] = useState(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
  const menuButtonRefs = useRef({});
  const [openPromptTemplateMenuId, setOpenPromptTemplateMenuId] =
    useState(null);
  const [promptTemplateMenuPosition, setPromptTemplateMenuPosition] = useState({
    top: 0,
    right: 0,
  });
  const promptTemplateMenuButtonRefs = useRef({});
  const sidebarRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();

  const handleNewChat = () => {
    // Clear active conversation when starting new chat
    setActiveConversationId(null);
    localStorage.removeItem("conversation_id");
    // Clear active chatbot when navigating to /home
    setActiveChatbotId(null);
    localStorage.removeItem("active_chatbot_id");
    navigate("/home", { state: { refresh: Date.now() } });
    onClose(); // Close mobile sidebar after navigation
  };

  const slugify = (value = "") =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  // Function to get current conversation ID from localStorage
  const getCurrentConversationId = () => {
    return localStorage.getItem("conversation_id");
  };

  // Function to update active conversation (only on /home route)
  const updateActiveConversation = useCallback(() => {
    const currentPath = window.location.pathname;
    const isHomeRoute =
      currentPath.endsWith("/home") || currentPath === "/home";
    // ||
    // currentPath.endsWith("/frontend/home");

    if (isHomeRoute) {
      const currentId = getCurrentConversationId();
      setActiveConversationId(currentId);
    } else {
      // Clear active conversation if not on /home
      setActiveConversationId(null);
    }
  }, []);

  // Extract fetchConversations so it can be called both from useEffect and imperatively
  const fetchConversations = async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !userId) return;

    // Check if API_BASE_URL is configured
    if (!process.env.REACT_APP_API_BASE_URL) {
      console.error("REACT_APP_API_BASE_URL is not configured");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        ENDPOINTS.GET_CONVERSATION_List(companyId, userId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      const data = await res.json();
      if (!res.ok) {
        console.error(data.detail || "Failed to fetch conversations");
        return;
      }

      setConversations(data || []);
    } catch (err) {
      console.error("Error fetching conversations:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchConversations();
    // Initialize active conversation from localStorage
    updateActiveConversation();
  }, [updateActiveConversation]);

  // Fetch prompt templates from API
  const fetchPromptTemplates = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const token = user?.access_token;

    if (!companyId || !token) return;

    try {
      const response = await fetch(ENDPOINTS.GET_PROMPT_TEMPLATES(companyId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPromptTemplates(data || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error fetching prompt templates:", errorData);
      }
    } catch (err) {
      console.error("Error calling prompt templates API:", err);
    }
  }, []);

  // Fetch chatbots from API
  const fetchChatbots = useCallback(async () => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !token) return;

    try {
      const res = await fetch(ENDPOINTS.GET_CHATBOTS(companyId, userId), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        console.error("Failed to fetch chatbots");
        return;
      }

      const data = await res.json();

      // Map API response to component format
      const mappedChatbots = Array.isArray(data)
        ? data.map((bot) => ({
            id: bot.id,
            name: bot.name,
            namespace: bot.namespace,
            description: bot.description,
            llm_settings: bot.llm_settings || {},
            is_default: bot.is_default || false,
            is_active: bot.is_active || false,
          }))
        : [];

      setChatbots(mappedChatbots);
    } catch (err) {
      console.error("Error fetching chatbots:", err);
    }
  }, []);

  // Helper function to fetch conversations for a chatbot
  const fetchChatbotConversations = useCallback(async (botId) => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;
    const token = user?.access_token;

    if (!companyId || !userId || !token) {
      return;
    }

    try {
      const response = await fetch(
        ENDPOINTS.GET_CONVERSATIONS_BY_CHATBOT(companyId, userId, botId),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        // Store conversations for this chatbot
        setChatbotConversations((prev) => ({
          ...prev,
          [botId]: data || [],
        }));
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error("Error fetching conversations by chatbot:", errorData);
      }
    } catch (err) {
      console.error("Error calling conversations by chatbot API:", err);
    }
  }, []);

  // Load chatbots and prompt templates on component mount
  useEffect(() => {
    fetchChatbots();
    fetchPromptTemplates();
    // Load active chatbot from localStorage
    const savedActiveChatbotId = localStorage.getItem("active_chatbot_id");
    if (savedActiveChatbotId) {
      setActiveChatbotId(savedActiveChatbotId);
    }
  }, [fetchChatbots, fetchPromptTemplates]);

  // Fetch conversations when activeChatbotId changes
  useEffect(() => {
    if (activeChatbotId && !chatbotConversations[activeChatbotId]) {
      fetchChatbotConversations(activeChatbotId);
    }
  }, [activeChatbotId, fetchChatbotConversations, chatbotConversations]);

  // Update active conversation from localStorage when route changes to chatbot
  useEffect(() => {
    const currentPath = location.pathname;
    const pathMatch = currentPath.match(/\/chatbot\/([^/]+)/);

    if (pathMatch) {
      // On chatbot route, ensure conversation from localStorage is active
      const savedConversationId = localStorage.getItem("conversation_id");
      if (savedConversationId) {
        setActiveConversationId(savedConversationId);
      }
    }
  }, [location]);

  // Sync active chatbot and conversation with route
  useEffect(() => {
    const checkRoute = () => {
      const currentPath = window.location.pathname;
      const pathMatch = currentPath.match(/\/chatbot\/([^/]+)/);
      const isHomeRoute =
        currentPath.endsWith("/home") || currentPath === "/home";
      //  ||
      // currentPath.endsWith("/frontend/home");

      if (pathMatch) {
        // On /chatbot/:id route, set active chatbot
        const chatbotIdFromRoute = pathMatch[1];
        setActiveChatbotId(chatbotIdFromRoute);
        localStorage.setItem("active_chatbot_id", chatbotIdFromRoute);
        // Keep active conversation if it exists
        const savedConversationId = localStorage.getItem("conversation_id");
        if (savedConversationId) {
          setActiveConversationId(savedConversationId);
        } else {
          setActiveConversationId(null);
        }
      } else if (isHomeRoute) {
        // On /home route, clear active chatbot
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
      } else {
        // On any other route, clear both active chatbot and conversation
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
        setActiveConversationId(null);
        localStorage.removeItem("conversation_id");
      }
    };

    checkRoute();

    const handleRouteChange = () => {
      checkRoute();
    };

    window.addEventListener("popstate", handleRouteChange);
    const interval = setInterval(checkRoute, 100);

    return () => {
      window.removeEventListener("popstate", handleRouteChange);
      clearInterval(interval);
    };
  }, []);

  // Monitor conversation changes
  useEffect(() => {
    const handleStorageChange = () => {
      updateActiveConversation();
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("focus", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("focus", handleStorageChange);
    };
  }, [updateActiveConversation]);

  // Get user role from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    setIsAdmin(user?.role === "admin");
  }, []);

  // Close dropdown menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickInChatbotMenuContainer = event.target.closest(
        ".chatbot-menu-container"
      );
      const isClickInPromptTemplateMenuContainer = event.target.closest(
        ".prompt-template-menu-container"
      );
      const isClickInChatbotDropdown = event.target.closest(
        '[class*="z-[9999]"]'
      );
      const isClickInPromptTemplateDropdown = event.target.closest(
        '[class*="z-[9998]"]'
      );

      if (
        !isClickInChatbotMenuContainer &&
        !isClickInPromptTemplateMenuContainer &&
        !isClickInChatbotDropdown &&
        !isClickInPromptTemplateDropdown
      ) {
        setOpenChatbotMenuId(null);
        setOpenPromptTemplateMenuId(null);
      }
    };

    if (openChatbotMenuId || openPromptTemplateMenuId) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [openChatbotMenuId, openPromptTemplateMenuId]);

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Do not close when clicking portaled dropdowns (Edit/Delete menus);
      // they render to document.body so are outside sidebarRef, but we must
      // keep the sidebar open so the Edit/Delete modals can open.
      const isClickInPortaledDropdown =
        event.target.closest('[class*="z-[9999]"]') ||
        event.target.closest('[class*="z-[9998]"]');
      if (
        isOpen &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        !isClickInPortaledDropdown
      ) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isOpen, onClose]);

  // Prevent body scroll when sidebar is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  // Re-fetch when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger) {
      fetchConversations();
    }
  }, [refreshTrigger]);

  // Expose refresh function globally (same as regular Sidebar)
  useEffect(() => {
    window.refreshMobileSidebarConversations = fetchConversations;

    window.refreshMobileChatbotConversations = (chatbotId) => {
      if (chatbotId) {
        fetchChatbotConversations(chatbotId);
      } else if (activeChatbotId) {
        fetchChatbotConversations(activeChatbotId);
      }
    };

    window.removeConversationFromMobileSidebar = (conversationId) => {
      setConversations((prevConversations) =>
        (prevConversations || []).filter((conv) => conv.id !== conversationId)
      );
    };

    window.updateMobileSidebarActiveConversation = (conversationId) => {
      setActiveConversationId(conversationId);
    };

    return () => {
      delete window.refreshMobileSidebarConversations;
      delete window.refreshMobileChatbotConversations;
      delete window.removeConversationFromMobileSidebar;
      delete window.updateMobileSidebarActiveConversation;
    };
  }, [activeChatbotId, fetchChatbotConversations]);

  // Reset prompt template form function
  const resetPromptTemplateForm = () => {
    setPromptTemplateName("");
    setPromptTemplate("");
    setEditingPromptTemplateId(null);
  };

  // Handle create prompt template
  const handleCreatePromptTemplate = async () => {
    if (!promptTemplateName.trim() || !promptTemplate.trim()) {
      showErrorToast("Please fill in all fields");
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    try {
      const isEditing = !!editingPromptTemplateId;
      const endpoint = isEditing
        ? ENDPOINTS.UPDATE_PROMPT_TEMPLATE(companyId, editingPromptTemplateId)
        : ENDPOINTS.CREATE_PROMPT_TEMPLATE(companyId);
      const method = isEditing ? "PUT" : "POST";

      const res = await fetch(endpoint, {
        method: method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          "X-User-Id": userId,
        },
        body: JSON.stringify({
          name: promptTemplateName.trim(),
          template: promptTemplate.trim(),
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        showErrorToast(
          data.detail ||
            `Failed to ${isEditing ? "update" : "create"} prompt template`
        );
        return;
      }

      showSuccessToast(
        isEditing
          ? "Prompt template updated successfully"
          : "Prompt template created successfully"
      );
      resetPromptTemplateForm();
      setIsPromptTemplateModalOpen(false);
      await fetchPromptTemplates();
    } catch (err) {
      console.error(
        `Error ${
          editingPromptTemplateId ? "updating" : "creating"
        } prompt template:`,
        err
      );
      showErrorToast(
        `Failed to ${
          editingPromptTemplateId ? "update" : "create"
        } prompt template`
      );
    }
  };

  // Handle copy prompt template
  const handleCopyPromptTemplate = (template) => {
    const textToCopy = template.template;

    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard
        .writeText(textToCopy)
        .then(() => {
          showSuccessToast("Template copied");
        })
        .catch(() => {
          fallbackCopyTextToClipboard(textToCopy);
        });
    } else {
      fallbackCopyTextToClipboard(textToCopy);
    }
  };

  // Fallback copy method
  const fallbackCopyTextToClipboard = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        showSuccessToast("Template copied");
      } else {
        showErrorToast("Failed to copy template");
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      showErrorToast("Failed to copy template");
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Handle edit prompt template
  const handleEditPromptTemplate = (template) => {
    setPromptTemplateName(template.name);
    setPromptTemplate(template.template);
    setEditingPromptTemplateId(template.id);
    setIsPromptTemplateModalOpen(true);
  };

  // Handle delete prompt template
  const handleDeletePromptTemplate = (template) => {
    setPromptTemplateToDelete(template);
    setIsDeletePromptTemplateModalOpen(true);
  };

  // Confirm and delete prompt template
  const confirmDeletePromptTemplate = async () => {
    if (!promptTemplateToDelete) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    try {
      const res = await fetch(
        ENDPOINTS.DELETE_PROMPT_TEMPLATE(companyId, promptTemplateToDelete.id),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "X-User-Id": userId,
          },
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        showErrorToast(data.detail || "Failed to delete prompt template");
        return;
      }

      showSuccessToast("Prompt template deleted successfully");
      setIsDeletePromptTemplateModalOpen(false);
      setPromptTemplateToDelete(null);
      await fetchPromptTemplates();
    } catch (err) {
      console.error("Error deleting prompt template:", err);
      showErrorToast("Failed to delete prompt template");
    }
  };

  // Reset form function
  const resetChatbotForm = () => {
    setNewChatbotName("");
    setChatbotNamespace("");
    setChatbotDescription("");
    setChatbotModel("gpt-5-mini");
    setChatbotTone("professional");
    setChatbotResponseLength("medium");
    setChatbotSystemPrompt("");
    setEditingChatbotId(null);
  };

  const handleCreateChatbot = async () => {
    const name = newChatbotName.trim();
    if (!name) {
      showErrorToast("Please enter a chatbot name");
      return;
    }

    if (!/^[a-zA-Z0-9\s]+$/.test(name)) {
      showErrorToast(
        "Chatbot name can only contain letters, numbers, and spaces (no special characters)"
      );
      return;
    }

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    const derivedNamespace = slugify(name);
    const finalNamespace = editingChatbotId
      ? chatbotNamespace.trim()
      : chatbotNamespace.trim() || derivedNamespace || `chatbot-${Date.now()}`;
    const description = chatbotDescription.trim();
    const systemPrompt =
      chatbotSystemPrompt.trim() || "You are a helpful assistant.";

    setLoading(true);
    try {
      const isEditing = editingChatbotId !== null;
      const endpoint = isEditing
        ? ENDPOINTS.UPDATE_CHATBOT(companyId, editingChatbotId)
        : ENDPOINTS.CREATE_CHATBOT(companyId);
      const method = isEditing ? "PUT" : "POST";

      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
          "X-User-Id": userId,
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name,
          namespace: finalNamespace,
          description,
          llm_settings: {
            system_prompt: systemPrompt,
            response_length: chatbotResponseLength,
            tone: chatbotTone,
            model: chatbotModel,
          },
          is_default: false,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        showErrorToast(
          data.detail ||
            data.message ||
            `Failed to ${isEditing ? "update" : "create"} chatbot`
        );
        return;
      }

      showSuccessToast(
        `Chatbot ${isEditing ? "updated" : "created"} successfully`
      );

      await fetchChatbots();

      if (!isEditing && data.id) {
        setActiveChatbotId(data.id);
        localStorage.setItem("active_chatbot_id", data.id);
        navigate(`/chatbot/${data.id}`);
        onClose(); // Close mobile sidebar after navigation
      }

      resetChatbotForm();
      setIsChatbotModalOpen(false);
    } catch (err) {
      console.error(
        `Error ${editingChatbotId ? "updating" : "creating"} chatbot:`,
        err
      );
      showErrorToast(
        `Failed to ${
          editingChatbotId ? "update" : "create"
        } chatbot. Please try again`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteChatbot = (botId) => {
    const chatbot = chatbots.find((bot) => bot.id === botId);
    if (chatbot) {
      setChatbotToDelete(chatbot);
      setIsDeleteChatbotModalOpen(true);
    }
  };

  // Confirm and delete chatbot
  const confirmDeleteChatbot = async () => {
    if (!chatbotToDelete) return;

    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const token = user?.access_token;
    const companyId = user?.company_id;
    const userId = user?.id || user?.user_id;

    if (!companyId || !token || !userId) {
      showErrorToast("Missing authentication details");
      return;
    }

    try {
      const response = await fetch(
        ENDPOINTS.DELETE_CHATBOT(companyId, chatbotToDelete.id),
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            "X-User-Id": userId,
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        showErrorToast(
          data.detail || data.message || "Failed to delete chatbot"
        );
        return;
      }

      showSuccessToast("Chatbot deleted successfully");
      setIsDeleteChatbotModalOpen(false);
      setChatbotToDelete(null);

      if (activeChatbotId === chatbotToDelete.id) {
        setActiveChatbotId(null);
        localStorage.removeItem("active_chatbot_id");
        navigate("/home");
        onClose(); // Close mobile sidebar after navigation
      }

      await fetchChatbots();
    } catch (err) {
      console.error("Error deleting chatbot:", err);
      showErrorToast("Failed to delete chatbot. Please try again");
    }
  };

  const handleChatbotClick = async (botId) => {
    setActiveChatbotId(botId);
    localStorage.setItem("active_chatbot_id", botId);
    setActiveConversationId(null);
    localStorage.removeItem("conversation_id");

    await fetchChatbotConversations(botId);

    navigate(`/chatbot/${botId}`);
    onClose(); // Close mobile sidebar after navigation
  };

  const handleEditChatbot = (botId, e) => {
    e?.stopPropagation();
    const bot = chatbots.find((b) => b.id === botId);
    if (!bot) return;

    setNewChatbotName(bot.name || "");
    setChatbotNamespace(bot.namespace || "");
    setChatbotDescription(bot.description || "");
    setChatbotModel(bot.llm_settings?.model || "gpt-5-mini");
    setChatbotTone(bot.llm_settings?.tone || "professional");
    setChatbotResponseLength(bot.llm_settings?.response_length || "medium");
    setChatbotSystemPrompt(bot.llm_settings?.system_prompt || "");

    setEditingChatbotId(botId);
    setIsChatbotModalOpen(true);
  };

  const handleConversationClick = (conv, chatbotId = null) => {
    setActiveConversationId(conv.id);
    localStorage.setItem("conversation_id", conv.id);

    if (chatbotId) {
      setActiveChatbotId(chatbotId);
      localStorage.setItem("active_chatbot_id", chatbotId);
      navigate(`/chatbot/${chatbotId}`, { state: { refresh: Date.now() } });

      setTimeout(() => {
        if (window.loadConversationData) {
          window.loadConversationData(conv.id);
        }
      }, 100);
    } else {
      setActiveChatbotId(null);
      localStorage.removeItem("active_chatbot_id");
      navigate("/home", { state: { refresh: Date.now() } });

      setTimeout(() => {
        if (window.loadConversationData) {
          window.loadConversationData(conv.id);
        }
      }, 100);
    }
    onClose(); // Close mobile sidebar after navigation
  };

  // When sidebar is closed, still render if prompt template or chatbot Edit/Delete
  // modals are open so they can show over the main layout (portaled to document.body).
  if (
    !isOpen &&
    !isPromptTemplateModalOpen &&
    !isDeletePromptTemplateModalOpen &&
    !isChatbotModalOpen &&
    !isDeleteChatbotModalOpen
  )
    return null;

  return (
    <>
      {/* Backdrop - only when sidebar is open */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={onClose}
        />
      )}

      {/* Sidebar */}
      <div
        ref={sidebarRef}
        className="fixed left-0 top-0 h-full w-64 bg-bg-light dark:bg-bg-darkSecondary z-50 md:hidden flex flex-col shadow-xl transform transition-transform duration-300 ease-in-out"
        style={{
          transform: isOpen ? "translateX(0)" : "translateX(-100%)",
        }}
      >
        {/* Header with Close Button */}
        <div className="flex items-center justify-between p-4 border-b border-gray-500">
          <span className="text-indigo-900 dark:text-white font-bold text-lg">
            Menu
          </span>
          <button
            onClick={onClose}
            className="p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          >
            <FiX className="text-xl" />
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="px-2 space-y-2 mt-4">
          <button
            onClick={handleNewChat}
            className="flex items-center gap-3 p-2 w-full text-left rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
          >
            <FiMessageSquare className="text-lg" />
            <span>New Chat</span>
          </button>

          <button
            className="flex items-center gap-3 p-2 w-full text-left rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-800 dark:text-gray-200"
            onClick={() => setIsSearchModalOpen(true)}
          >
            <FiSearch className="text-lg" />
            <span>Search</span>
          </button>
        </nav>

        {/* Prompt Template Section */}
        <div className="px-2 mt-6 space-y-3">
          <button
            onClick={() => setShowPromptTemplates((prev) => !prev)}
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>Prompt Template</span>
            {showPromptTemplates ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showPromptTemplates && (
            <div className="no-scrollbar max-h-40 overflow-y-auto">
              <ul className="space-y-1 pl-2">
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => {
                        resetPromptTemplateForm();
                        setIsPromptTemplateModalOpen(true);
                      }}
                      className="w-full flex items-center gap-2 truncate text-sm p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                    >
                      <FiPlus className="text-base" />
                      <span className="truncate">Create Prompt Template</span>
                    </button>
                  </li>
                )}

                {promptTemplates.length === 0 ? (
                  <li className="text-xs text-gray-500 dark:text-gray-400 px-2 py-2">
                    No prompt templates yet.
                  </li>
                ) : (
                  promptTemplates.map((template) => (
                    <li
                      key={template.id}
                      className="flex items-center justify-between truncate text-sm p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
                    >
                      <span className="truncate flex-1" title={template.name}>
                        {template.name}
                      </span>
                      <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                        <FiCopy
                          className="text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 cursor-pointer"
                          title="Copy template"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyPromptTemplate(template);
                          }}
                        />
                        {isAdmin && (
                          <div
                            className="prompt-template-menu-container relative"
                            ref={(el) => {
                              promptTemplateMenuButtonRefs.current[
                                template.id
                              ] = el;
                            }}
                          >
                            <HiDotsHorizontal
                              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-white cursor-pointer"
                              title="More options"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openPromptTemplateMenuId === template.id) {
                                  setOpenPromptTemplateMenuId(null);
                                } else {
                                  const buttonEl =
                                    promptTemplateMenuButtonRefs.current[
                                      template.id
                                    ];
                                  if (buttonEl) {
                                    const rect =
                                      buttonEl.getBoundingClientRect();
                                    setPromptTemplateMenuPosition({
                                      top: rect.bottom + 8,
                                      right: window.innerWidth - rect.right,
                                    });
                                  }
                                  setOpenPromptTemplateMenuId(template.id);
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </li>
                  ))
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Chatbots Section */}
        <div className="px-2 mt-3 space-y-3">
          <button
            onClick={() => setShowChatbots((prev) => !prev)}
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>Chatbots</span>
            {showChatbots ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showChatbots && (
            <div className="no-scrollbar max-h-40 overflow-y-auto">
              <ul className="space-y-1 pl-2">
                {isAdmin && (
                  <li>
                    <button
                      onClick={() => {
                        resetChatbotForm();
                        setIsChatbotModalOpen(true);
                      }}
                      className="w-full flex items-center gap-2 truncate text-sm p-2 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-800 transition-colors"
                    >
                      <FiPlus className="text-base" />
                      <span className="truncate">New Chatbot</span>
                    </button>
                  </li>
                )}

                {chatbots.length === 0 ? (
                  <li className="text-xs text-gray-500 dark:text-gray-400 px-2 py-2">
                    No chatbots yet.
                  </li>
                ) : (
                  chatbots.map((bot) => {
                    const isActive = activeChatbotId === bot.id;
                    const botConversations = chatbotConversations[bot.id] || [];
                    return (
                      <li key={bot.id} className="space-y-1">
                        <div
                          onClick={() => handleChatbotClick(bot.id)}
                          className={`flex items-center justify-between truncate text-sm p-2 rounded-md transition-colors cursor-pointer ${
                            isActive
                              ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border-l-4 border-indigo-500"
                              : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                          }`}
                          title={bot.name}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <SiChatbot
                              className={`flex-shrink-0 ${
                                isActive
                                  ? "text-indigo-600 dark:text-white"
                                  : "text-text-dark dark:text-white"
                              }`}
                            />
                            <span className="truncate">{bot.name}</span>
                          </div>
                          {isAdmin && (
                            <div
                              className="chatbot-menu-container relative flex-shrink-0 ml-2"
                              ref={(el) => {
                                menuButtonRefs.current[bot.id] = el;
                              }}
                            >
                              <HiDotsHorizontal
                                className={`${
                                  isActive
                                    ? "text-indigo-700 hover:text-indigo-900 dark:text-indigo-200 dark:hover:text-indigo-100"
                                    : "text-text-dark hover:text-gray-700 dark:text-gray-400 dark:hover:text-white"
                                } cursor-pointer`}
                                title="More options"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (openChatbotMenuId === bot.id) {
                                    setOpenChatbotMenuId(null);
                                  } else {
                                    const buttonEl =
                                      menuButtonRefs.current[bot.id];
                                    if (buttonEl) {
                                      const rect =
                                        buttonEl.getBoundingClientRect();
                                      setMenuPosition({
                                        top: rect.bottom + 8,
                                        right: window.innerWidth - rect.right,
                                      });
                                    }
                                    setOpenChatbotMenuId(bot.id);
                                  }
                                }}
                              />
                            </div>
                          )}
                        </div>
                        {botConversations.length > 0 && (
                          <ul className="ml-4 space-y-1 pl-2 border-l-2 border-gray-300 dark:border-gray-600">
                            {botConversations.map((conv) => {
                              const isOnChatbotRoute =
                                location.pathname.includes(
                                  `/chatbot/${bot.id}`
                                );
                              const isConvActive =
                                activeConversationId === conv.id &&
                                isOnChatbotRoute &&
                                activeChatbotId === bot.id;
                              return (
                                <li
                                  key={conv.id}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleConversationClick(conv, bot.id);
                                  }}
                                  className={`flex items-center justify-between truncate text-xs p-1.5 rounded-md cursor-pointer transition-colors ${
                                    isConvActive
                                      ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100"
                                      : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-400"
                                  }`}
                                  title={conv.title}
                                >
                                  <span className="truncate">{conv.title}</span>
                                  <FiTrash2
                                    className={`mr-2 flex-shrink-0 ${
                                      isConvActive
                                        ? "text-red-600 hover:text-red-800"
                                        : "text-red-500 hover:text-red-700"
                                    } cursor-pointer`}
                                    title="Delete conversation"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (
                                        typeof onRequestDeleteConversation ===
                                        "function"
                                      ) {
                                        onRequestDeleteConversation(conv);
                                        setChatbotConversations((prev) => ({
                                          ...prev,
                                          [bot.id]: (prev[bot.id] || []).filter(
                                            (c) => c.id !== conv.id
                                          ),
                                        }));
                                      }
                                    }}
                                  />
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })
                )}
              </ul>
            </div>
          )}
        </div>

        {/* Scrollable History Section */}
        <div className="px-2 pt-3 flex-1 overflow-y-auto no-scrollbar">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
          >
            <span>General Chat</span>
            {showHistory ? (
              <FiChevronDown className="text-gray-600 dark:text-gray-400" />
            ) : (
              <FiChevronUp className="text-gray-600 dark:text-gray-400" />
            )}
          </button>

          {showHistory && (
            <ul className="mt-2 space-y-1 pl-2">
              {loading ? (
                <li className="text-sm text-gray-500 dark:text-gray-400">
                  Loading conversations...
                </li>
              ) : conversations.length > 0 ? (
                conversations.map((conv) => {
                  const isActive = activeConversationId === conv.id;
                  return (
                    <li
                      key={conv.id}
                      onClick={() => handleConversationClick(conv)}
                      className={`flex items-center justify-between truncate text-sm p-2 rounded-md cursor-pointer transition-colors ${
                        isActive
                          ? "bg-indigo-100 dark:bg-indigo-900 text-indigo-900 dark:text-indigo-100 border-l-4 border-indigo-500"
                          : "hover:bg-gray-200 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300"
                      }`}
                    >
                      <span className="truncate">{conv.title}</span>
                      <FiTrash2
                        className={`${
                          isActive
                            ? "text-red-600 hover:text-red-800"
                            : "text-red-500 hover:text-red-700"
                        } cursor-pointer ml-2 flex-shrink-0`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            typeof onRequestDeleteConversation === "function"
                          ) {
                            onRequestDeleteConversation(conv);
                          }
                        }}
                      />
                    </li>
                  );
                })
              ) : (
                <li className="text-sm text-gray-500 dark:text-gray-400">
                  No conversations found.
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Search Modal */}
        <SearchModal
          isOpen={isSearchModalOpen}
          onClose={() => setIsSearchModalOpen(false)}
        />

        {/* Delete Prompt Template Confirmation Modal - portaled to body so it shows over main layout when sidebar is closed */}
        {isDeletePromptTemplateModalOpen &&
          promptTemplateToDelete &&
          createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
                <button
                  onClick={() => {
                    setIsDeletePromptTemplateModalOpen(false);
                    setPromptTemplateToDelete(null);
                  }}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                >
                  <FiX size={18} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Delete Prompt Template
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete the prompt template{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    "{promptTemplateToDelete.name}"
                  </span>
                  ? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setIsDeletePromptTemplateModalOpen(false);
                      setPromptTemplateToDelete(null);
                    }}
                    className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeletePromptTemplate}
                    className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* Delete Chatbot Confirmation Modal - portaled to body so it shows over main layout when sidebar is closed */}
        {isDeleteChatbotModalOpen &&
          chatbotToDelete &&
          createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
                <button
                  onClick={() => {
                    setIsDeleteChatbotModalOpen(false);
                    setChatbotToDelete(null);
                  }}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                >
                  <FiX size={18} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Delete Chatbot
                </h2>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
                  Are you sure you want to delete the chatbot{" "}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    "{chatbotToDelete.name}"
                  </span>
                  ? This action cannot be undone.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => {
                      setIsDeleteChatbotModalOpen(false);
                      setChatbotToDelete(null);
                    }}
                    className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmDeleteChatbot}
                    className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* Prompt Template Modal (Edit/Create) - portaled to body so it shows over main layout when sidebar is closed */}
        {isPromptTemplateModalOpen &&
          createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative">
                <button
                  onClick={() => {
                    setIsPromptTemplateModalOpen(false);
                    resetPromptTemplateForm();
                  }}
                  className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
                >
                  <FiX size={18} />
                </button>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  {editingPromptTemplateId
                    ? "Update Prompt Template"
                    : "Create Prompt Template"}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Name
                    </label>
                    <input
                      type="text"
                      value={promptTemplateName}
                      onChange={(e) => setPromptTemplateName(e.target.value)}
                      placeholder="e.g. Customer Service"
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Template
                    </label>
                    <textarea
                      value={promptTemplate}
                      onChange={(e) => setPromptTemplate(e.target.value)}
                      placeholder="You are a helpful customer service agent."
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[120px]"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-3 mt-6">
                  <button
                    onClick={() => {
                      setIsPromptTemplateModalOpen(false);
                      resetPromptTemplateForm();
                    }}
                    className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreatePromptTemplate}
                    className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                    disabled={
                      !promptTemplateName.trim() || !promptTemplate.trim()
                    }
                  >
                    {editingPromptTemplateId
                      ? "Update Prompt Template"
                      : "Create Prompt Template"}
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )}

        {/* New Chatbot Modal (Edit/Create) - portaled to body so it shows over main layout when sidebar is closed */}
        {isChatbotModalOpen &&
          createPortal(
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black bg-opacity-50 px-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
              <button
                onClick={() => {
                  setIsChatbotModalOpen(false);
                  resetChatbotForm();
                }}
                className="absolute top-3 right-3 text-gray-500 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white"
              >
                <FiX size={18} />
              </button>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                {editingChatbotId ? "Update Chatbot" : "Create Chatbot"}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Chatbot Name
                  </label>
                  <input
                    type="text"
                    value={newChatbotName}
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        /[^a-zA-Z0-9\s]/g,
                        ""
                      );
                      setNewChatbotName(value);
                      if (!editingChatbotId) {
                        setChatbotNamespace(slugify(value));
                      }
                    }}
                    placeholder="e.g. Legal Assistant"
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Namespace
                  </label>
                  <input
                    readOnly={!!editingChatbotId}
                    type="text"
                    value={chatbotNamespace}
                    onChange={(e) => {
                      if (!editingChatbotId) {
                        setChatbotNamespace(slugify(e.target.value));
                      }
                    }}
                    placeholder="auto-generated"
                    className={`w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                      editingChatbotId
                        ? "bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                        : ""
                    }`}
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Description
                  </label>
                  <textarea
                    value={chatbotDescription}
                    onChange={(e) => setChatbotDescription(e.target.value)}
                    placeholder="What is this chatbot for?"
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    Model
                  </label>
                  <select
                    value={chatbotModel}
                    onChange={(e) => setChatbotModel(e.target.value)}
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="gpt-5-mini">gpt-5-mini</option>
                    <option value="gpt-5-nano">gpt-5-nano</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano</option>
                    <option value="gpt-4o-mini">gpt-4o-mini</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                    System Prompt
                  </label>
                  <textarea
                    value={chatbotSystemPrompt}
                    onChange={(e) => setChatbotSystemPrompt(e.target.value)}
                    placeholder="You are a helpful assistant."
                    className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 min-h-[80px]"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Response length
                    </label>
                    <select
                      value={chatbotResponseLength}
                      onChange={(e) => setChatbotResponseLength(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="short">short</option>
                      <option value="medium">medium</option>
                      <option value="long">long</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm text-gray-600 dark:text-gray-300 mb-2">
                      Tone
                    </label>
                    <select
                      value={chatbotTone}
                      onChange={(e) => setChatbotTone(e.target.value)}
                      className="w-full px-3 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="professional">professional</option>
                      <option value="encouraging">encouraging</option>
                      <option value="persuasive">persuasive</option>
                      <option value="neutral">neutral</option>
                      <option value="empathetic">empathetic</option>
                      <option value="authoritative">authoritative</option>
                      <option value="reflective">reflective</option>
                      <option value="sympathetic">sympathetic</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setIsChatbotModalOpen(false);
                    resetChatbotForm();
                  }}
                  className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 dark:bg-gray-700 dark:text-gray-200 hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateChatbot}
                  className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                  disabled={!newChatbotName.trim()}
                >
                  {editingChatbotId ? "Update Chatbot" : "Create Chatbot"}
                </button>
              </div>
            </div>
          </div>,
            document.body
          )}

        {/* Dropdown Menu Portal */}
        {openChatbotMenuId &&
          createPortal(
            <div
              className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 min-w-[120px] py-1"
              style={{
                top: `${menuPosition.top}px`,
                right: `${menuPosition.right}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditChatbot(openChatbotMenuId, e);
                  setOpenChatbotMenuId(null);
                  onClose(); // Close sidebar so modal shows over main layout
                }}
              >
                <FiEdit2 className="text-base flex-shrink-0" />
                <span>Edit</span>
              </button>
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteChatbot(openChatbotMenuId);
                  setOpenChatbotMenuId(null);
                  onClose(); // Close sidebar so modal shows over main layout
                }}
              >
                <FiTrash2 className="text-base flex-shrink-0" />
                <span>Delete</span>
              </button>
            </div>,
            document.body
          )}

        {/* Prompt Template Dropdown Menu Portal */}
        {openPromptTemplateMenuId &&
          createPortal(
            <div
              className="fixed z-[9998] bg-white dark:bg-gray-800 rounded-md shadow-xl border border-gray-200 dark:border-gray-700 min-w-[120px] py-1"
              style={{
                top: `${promptTemplateMenuPosition.top}px`,
                right: `${promptTemplateMenuPosition.right}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  const template = promptTemplates.find(
                    (t) => t.id === openPromptTemplateMenuId
                  );
                  if (template) {
                    handleEditPromptTemplate(template);
                  }
                  setOpenPromptTemplateMenuId(null);
                  onClose(); // Close sidebar so modal shows over main layout
                }}
              >
                <FiEdit2 className="text-base flex-shrink-0" />
                <span>Edit</span>
              </button>
              <button
                className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                onClick={(e) => {
                  e.stopPropagation();
                  const template = promptTemplates.find(
                    (t) => t.id === openPromptTemplateMenuId
                  );
                  if (template) {
                    handleDeletePromptTemplate(template);
                  }
                  setOpenPromptTemplateMenuId(null);
                  onClose(); // Close sidebar so modal shows over main layout
                }}
              >
                <FiTrash2 className="text-base flex-shrink-0" />
                <span>Delete</span>
              </button>
            </div>,
            document.body
          )}
      </div>
    </>
  );
}
